import { useState } from 'react';
import { X, Save, Loader2, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { avatarModelsService, type AvatarModel } from '@/services/avatar-models.service';

interface Props {
  avatar: AvatarModel;
  onClose: () => void;
  /** Called after a successful save so the parent can re-fetch. */
  onSaved?: () => void;
}

export default function EditPrivateAvatarModal({ avatar, onClose, onSaved }: Props) {
  const [name, setName] = useState(avatar.name || '');
  const [gender, setGender] = useState<'' | 'male' | 'female' | 'other'>((avatar.gender || '') as any);
  const [is_saving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const has_changes =
    name.trim() !== (avatar.name || '').trim() ||
    (gender || '') !== (avatar.gender || '');

  const handleSave = async () => {
    if (!has_changes || is_saving) return;
    if (name.trim().length < 2) {
      setError('Name must be at least 2 characters.');
      return;
    }
    setIsSaving(true);
    setError(null);
    toast.loading('Saving changes...', { id: 'edit-avatar' });

    const updates: { name?: string; gender?: 'male' | 'female' | 'other' } = {};
    if (name.trim() !== (avatar.name || '').trim()) updates.name = name.trim();
    if ((gender || '') !== (avatar.gender || '')) {
      if (gender) updates.gender = gender;
    }

    const result = await avatarModelsService.updatePrivateAvatar(avatar.id, updates);
    setIsSaving(false);

    if (!result.success) {
      setError(result.error || 'Update failed');
      toast.error(result.error || 'Update failed', { id: 'edit-avatar' });
      return;
    }

    toast.success('Avatar updated', { id: 'edit-avatar' });
    onSaved?.();
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl w-[95vw] max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b">
          <h2 className="text-sm font-semibold text-gray-900">Edit Avatar</h2>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors">
            <X className="w-4 h-4 text-gray-500" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4">
          {/* Preview */}
          <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
            <img
              src={avatar.preview_image_url}
              alt={avatar.name}
              className="w-16 h-16 rounded-lg object-cover border border-gray-200"
            />
            <div className="flex-1 min-w-0">
              <div className="text-[10px] text-gray-400 truncate">ID: {avatar.id}</div>
              <div className="text-xs text-gray-600 mt-0.5">
                Photo Avatar · HeyGen
              </div>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Display name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={80}
              className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 bg-white focus:ring-1 focus:ring-purple-300 focus:border-purple-300"
              disabled={is_saving}
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Gender</label>
            <select
              value={gender}
              onChange={(e) => setGender(e.target.value as '' | 'male' | 'female' | 'other')}
              className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 bg-white"
              disabled={is_saving}
            >
              <option value="">Prefer not to say</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
              <option value="other">Other</option>
            </select>
          </div>

          {error && (
            <div className="p-2.5 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700 flex items-start gap-2">
              <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          <p className="text-[10px] text-gray-400 leading-relaxed">
            Editing only updates how this avatar shows in your library. HeyGen keeps the original name internally — this label is what your team sees.
          </p>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t bg-gray-50">
          <button
            type="button"
            onClick={onClose}
            disabled={is_saving}
            className="px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!has_changes || is_saving}
            className="px-4 py-1.5 bg-purple-600 text-white rounded-lg text-xs font-medium hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center gap-1.5"
          >
            {is_saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            {is_saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>
    </div>
  );
}
